/**
 * Connects game signals to SFX playback. Audio is a passive observer here —
 * it only subscribes to Rive events, so the game orchestrator stays unaware of
 * sound.
 *
 * Map: Rive event name → SFX clip base name (file in public/assets/dragontiger_sfx/).
 *
 *   FlipCard           → FlipCard         a card is flipped open
 *   StartCard          → StartCard        cards dealt out at round start
 *   DragonTigerStart   → DragonTigerStart dragon-vs-tiger reveal begins
 *   DragonTigerWin     → DragonTigerWin   winning side announced
 *   YouWinStart        → YouWin           win popup appears (MainWinLose.riv)
 *   YouLoseStart       → YouLose          lose popup appears (MainWinLose.riv)
 *
 * The chip-denomination select sound (`ClickBetSelect`) is NOT here: the chip
 * picker is DOM-driven (TableControls → GameState.selectChip), not a Rive
 * event, so main.ts plays it from a GameState subscription. `CHIP_SELECT_SFX`
 * is exported so it gets preloaded with the rest.
 *
 * NOTE — event names verified via `Tools/rive introspect` (card/result cues on
 * dragontiger.riv; YouWin/YouLose on MainWinLose.riv). Every Rive event is also
 * logged as `[Rive][event:passthrough]` (riveLayer.ts); if a sound never fires,
 * check the console for the real event name.
 */
import type { RiveLayer } from '../rive/riveLayer';
import type { SoundManager } from './soundManager';

/** Rive event name → SFX clip base name. */
const EVENT_SFX: Readonly<Record<string, string>> = {
  FlipCard: 'FlipCard',
  StartCard: 'StartCard',
  DragonTigerStart: 'DragonTigerStart',
  DragonTigerWin: 'DragonTigerWin',
  YouWinStart: 'YouWin',
  YouLoseStart: 'YouLose',
};

/** Clip played when the selected chip changes (driven from GameState). */
export const CHIP_SELECT_SFX = 'ClickBetSelect';

/** Distinct clips referenced anywhere — feed to SoundManager.preload(). */
export const SFX_CLIPS: readonly string[] = Array.from(
  new Set([...Object.values(EVENT_SFX), CHIP_SELECT_SFX]),
);

/** Subscribe SFX playback to the relevant Rive events. */
export function bindGameSounds(rive: RiveLayer, sound: SoundManager): void {
  for (const [event, clip] of Object.entries(EVENT_SFX)) {
    rive.on(event, () => sound.play(clip));
  }
}
